<template>
  <page-template :is-loading="isLoading" :data-exists="selectedActionData !== null">
    <template #pageHeadline>
      {{
        isNew ? $t('action.configPage.newPageHeadline') : $t('action.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft v-if="!readOnly">
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Actions List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedActionData && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          v-if="selectedAction"
          :items="dropdownData.map((item) => [item.id, item.name] as [string, string])"
          :disabled="isLoading"
          v-model:value="selectedAction"
          @update:value="switchAction($event as string)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('action.configPage.switchTooltip')"
          classes="switch-action switch-page-entry"
          data-qa="switch-action"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight v-if="!readOnly">
      <template v-if="selectedActionData && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateAction()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadAction()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteActionModal()"
          class="delete-button"
          :disabled="isLoading || selectedActionProtected || selectedActionReferenced"
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isActionValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('action.configPage.name')"
              :placeholder="$t('action.configPage.name')"
              v-model:inputValue="selectedActionData.name"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('action.configPage.description')"
              v-model:inputValue="selectedActionData.description"
              rows="2"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              v-if="selectedActionData.type"
              :items="actionTypes"
              v-model:value="selectedActionData.type"
              @update:value="
                resetParams($event as Action['type'], true);
                reloadHeadersList();
              "
              :label="$t('action.configPage.type')"
              classes="type-selection"
              data-qa="type-selection"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field tags-selection-field">
            <tags-selection
              :value="selectedActionData.tags"
              @update:value="selectedActionData.tags = $event as string[]"
              :label="$t('action.configPage.tags')"
              :disabled="readOnly"
            />
          </div>
        </div>
        <div class="rbz-column is-8">
          <template v-if="['monitor', 'block'].includes(selectedActionData.type)">
            <div
              v-if="
                selectedActionData.type && actionsWithStatusCode.includes(selectedActionData.type)
              "
              class="rbz-field status-code-field"
            >
              <rbz-form-input
                name="status-code"
                type="number"
                number-type="whole"
                classes="status-code"
                data-qa="status-code-input"
                :label="$t('action.statusCodeField')"
                :placeholder="$t('action.statusCodeField')"
                :is-invalid="!isValidStatusCode && fieldsTouched.includes('statusCode')"
                v-model:inputValue="selectedActionData.params.status"
                :disabled="readOnly"
                ref="statusCode"
                @blur="setFieldTouched('statusCode')"
              />
              <div
                v-if="!isValidStatusCode && fieldsTouched.includes('statusCode')"
                class="status-code-invalid rbz-text-medium rbz-text-danger mb-2"
              >
                {{
                  selectedActionData.params.status == null ||
                  selectedActionData.params.status === ''
                    ? $t('action.configPage.emptyStatusCode')
                    : $t('action.configPage.invalidStatusCode')
                }}
              </div>
            </div>
            <div class="rbz-field headers-field" :class="{ 'is-active': headersFieldsActive }">
              <label class="rbz-label">
                {{
                  selectedActionData.type === 'block'
                    ? $t('action.configPage.headers.responseTitle')
                    : $t('action.configPage.headers.requestTitle')
                }}
              </label>
              <div v-for="(header, index) in headersArray" :key="header.id" class="rbz-columns">
                <div class="rbz-column is-3">
                  <rbz-form-input
                    name="header-key"
                    classes="header-key"
                    data-qa="header-key-input"
                    :placeholder="$t('action.configPage.headers.key')"
                    :is-invalid="!isHeaderKeyValid(header)"
                    @focus="headersFieldsActive = true"
                    @blur="headersFieldsActive = false"
                    v-model:inputValue="header.key"
                    @update:inputValue="trimHeaderKey(header, $event as string)"
                    :disabled="readOnly"
                  />
                </div>
                <div class="rbz-column">
                  <rbz-form-input
                    name="header-value"
                    classes="header-value"
                    data-qa="header-value-input"
                    :placeholder="$t('action.configPage.headers.value')"
                    :is-invalid="!isHeaderValueValid(header)"
                    @focus="headersFieldsActive = true"
                    @blur="headersFieldsActive = false"
                    v-model:inputValue="header.value"
                    :disabled="readOnly"
                  />
                </div>
                <div class="rbz-column is-narrow align-items-center">
                  <rbz-form-button
                    classes="remove-header"
                    icon="IconDelete"
                    variant="tertiary"
                    data-qa="remove-header-button"
                    @button-click="removeHeader(index)"
                    :disabled="readOnly"
                    v-b-tooltip="readOnly ? '' : $t('action.configPage.headers.remove')"
                  />
                </div>
              </div>
              <rbz-form-button
                :label="$t('action.configPage.headers.add')"
                classes="add-header-button"
                data-qa="add-header-button"
                variant="secondary"
                @button-click="addHeader()"
                :disabled="readOnly"
              />
            </div>
            <div v-if="selectedActionData.type === 'block'" class="rbz-field content-field">
              <rbz-form-textarea
                name="content"
                classes="content"
                data-qa="content-textarea"
                :label="$t('action.configPage.content')"
                v-model:inputValue="selectedActionData.params.content"
                :disabled="readOnly"
                rows="20"
              />
            </div>
          </template>
        </div>
      </div>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="
          loadAction(selectedAction);
          loadDropdownData();
        "
        :disabled="readOnly"
      />
      <rbz-modal-dialog
        :show="showDeleteActionModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteAction"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteActionModal = false"
        @closeModal="showDeleteActionModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedActionData.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import { BasicDocument, Action, DocumentName } from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import TagsSelection from '@/components/TagsSelection.vue';
import GitHistory from '@/components/GitHistory.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { useTagsStore } from '@/store/TagsStore';
import { useRouting } from '@/helpers/RoutingUtils';

type HeaderObject = {
  id: string;
  key: string;
  value: string;
};

export default defineComponent({
  name: 'ActionsEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    GitHistory,
    TagsSelection,
    RbzFormButton,
    RbzFormInput,
    RbzFormTextarea,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const actionTypes = [
      ['skip', this.$t('action.typeValue.skip')],
      ['block', this.$t('action.typeValue.block')],
      ['challenge', this.$t('action.typeValue.challenge')],
      ['ichallenge', this.$t('action.typeValue.ichallenge')],
      ['monitor', this.$t('action.typeValue.monitor')],
    ] as [Action['type'], string][];
    return {
      dropdownData: [] as BasicDocument[],
      selectedAction: null as string,
      selectedActionData: null as Action,
      originalActionData: null as Action,
      pageLoadingCounter: 0,
      isDeleteLoading: false,
      showDeleteActionModal: false,

      actionTypes: actionTypes,
      headersArray: [] as HeaderObject[],
      referencedIds: [] as string[],
      headersFieldsActive: false,
      isNew: false,
      fieldsTouched: [] as string[],
      actionsWithStatusCode: ['block'] as Action['type'][],
      minStatusCode: 100,
      maxStatusCode: 599,
    };
  },
  watch: {
    headersArray: {
      handler: function (value: HeaderObject[]) {
        if (!['block', 'monitor'].includes(this.selectedActionData.type)) {
          return;
        }
        const uniqHeaders = _.uniqBy(value, 'key');
        const emptyKey = uniqHeaders.find((header) => {
          return !header.key;
        });
        if (uniqHeaders.length !== value.length || emptyKey) {
          return;
        }
        this.selectedActionData.params.headers = _.reduce(
          value,
          (result, header) => {
            result[header.key] = header.value;
            return result;
          },
          {} as Action['params']['headers']
        );
      },
      deep: true,
    },
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useTagsStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName(): DocumentName {
      return {
        plural: this.$t('action.plural'),
        singular: this.$t('action.singular'),
      };
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/actions/versions`;
    },

    isLoading(): boolean {
      return this.pageLoadingCounter > 0 || this.isDeleteLoading;
    },

    selectedActionProtected(): boolean {
      return this.selectedAction?.startsWith('action-');
    },

    selectedActionReferenced(): boolean {
      return this.referencedIds.includes(this.selectedAction);
    },

    deleteTooltip(): string {
      if (this.selectedActionProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.selectedActionReferenced) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return '';
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedActionData, this.originalActionData);
    },

    isHeadersListValid(): boolean {
      const anyKeyInvalid = _.some(this.headersArray, (header) => {
        return !this.isHeaderKeyValid(header);
      });
      const anyValueInvalid = _.some(this.headersArray, (header) => {
        return !this.isHeaderValueValid(header);
      });
      return !anyKeyInvalid && !anyValueInvalid;
    },

    isValidStatusCode(): boolean {
      if (!this.actionsWithStatusCode.includes(this.selectedActionData.type)) return true;

      // check for whole numbers, ensure for TypeScript that type is a number and not an empty string
      const statusCode = this.selectedActionData.params?.status;
      if (statusCode === '' || statusCode == null) return false;

      const status = Number(statusCode);
      if (!Number.isInteger(status)) return false;

      return status >= this.minStatusCode && status <= this.maxStatusCode;
    },

    isActionValid(): boolean {
      const trimmedName = this.selectedActionData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedActionData.name &&
        (trimmedName === this.originalActionData.name.toLowerCase().trim() || !nameExists);

      return this.isHeadersListValid && nameValid && this.isValidStatusCode;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('actions'));
    },

    switchAction(actionId: string) {
      this.navigateTo(this.getEditPath('actions', actionId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/actions`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    async loadAction(actionId: string) {
      this.setPageLoadingStatus(true);
      this.fieldsTouched = [];
      this.selectedAction = actionId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/actions/${actionId}`,
      });
      this.selectedActionData = response?.data || {};
      if (!this.selectedActionData.params) {
        this.resetParams(this.selectedActionData.type);
      }
      if (this.selectedActionData.params?.status) {
        this.selectedActionData.params.status = Number(this.selectedActionData.params.status);
      }
      this.originalActionData = _.cloneDeep(this.selectedActionData);
      this.reloadHeadersList();
      this.setPageLoadingStatus(false);
    },

    async duplicateAction() {
      this.setPageLoadingStatus(true);
      const actionToAdd = _.cloneDeep(this.selectedActionData);
      actionToAdd.id = DatasetsUtils.generateUUID2();
      actionToAdd.name = Utils.generateUniqueEntityName(
        actionToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/actions/${actionToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        methodName: 'POST',
        url,
        data: actionToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchAction(actionToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadAction() {
      Utils.downloadFile('action', 'json', this.selectedActionData);
    },

    openDeleteActionModal() {
      this.showDeleteActionModal = true;
    },

    async deleteAction() {
      this.isDeleteLoading = true;
      const actionId = this.selectedAction;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === actionId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedAction = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/actions/${actionId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }

      this.selectedActionData.name = this.selectedActionData.name.trim();
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/actions/${this.selectedActionData.id}`,
        data: this.selectedActionData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace('new', `config/${this.selectedActionData.id}`);
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
        }
      }
      this.originalActionData = _.cloneDeep(this.selectedActionData);
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      let response;
      response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/acl-profiles`,
        config: { headers: { 'x-fields': 'action' } },
      });
      const aclProfiles = response?.data?.items || [];
      response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/global-filters`,
        config: { headers: { 'x-fields': 'action' } },
      });
      const globalFilters = response?.data?.items || [];
      response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/rate-limit-rules`,
        config: { headers: { 'x-fields': 'action' } },
      });
      const rateLimitRules = response?.data?.items || [];
      response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/content-filter-profiles`,
        config: { headers: { 'x-fields': 'action' } },
      });
      const contentFilterProfiles = response?.data?.items || [];
      response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/dynamic-rules`,
        config: { headers: { 'x-fields': 'action' } },
      });
      const dynamicRules = response?.data?.items || [];
      this.referencedIds = _.uniq([
        ..._.map(aclProfiles, 'action'),
        ..._.map(globalFilters, 'action'),
        ..._.map(rateLimitRules, 'action'),
        ..._.map(contentFilterProfiles, 'action'),
        ..._.map(dynamicRules, 'action'),
      ]);
    },

    resetParams(type: Action['type'], override?: boolean) {
      this.fieldsTouched = [];
      if (type === 'block') {
        if (!this.selectedActionData.params || override) {
          this.selectedActionData.params = {
            headers: {
              'content-type': 'text/html',
            },
            status: '',
            content: '',
          };
        }
      } else if (type === 'monitor') {
        if (!this.selectedActionData.params || override) {
          this.selectedActionData.params = {
            headers: {},
          };
        }
      } else {
        delete this.selectedActionData.params;
      }
    },

    reloadHeadersList() {
      this.headersArray = _.map(this.selectedActionData?.params?.headers, function (value, key) {
        return {
          id: DatasetsUtils.generateUUID2(),
          key: key,
          value: value,
        };
      });
    },

    removeHeader(index: number): void {
      this.headersArray.splice(index, 1);
    },

    addHeader(): void {
      this.headersArray.push({
        id: DatasetsUtils.generateUUID2(),
        key: '',
        value: '',
      });
    },

    isHeaderKeyValid(header: HeaderObject): boolean {
      const keys = _.map(this.headersArray, 'key');
      const duplicateKeys = _.filter(keys, (val, i, iteratee) => _.includes(iteratee, val, i + 1));
      return header.key?.length > 0 && !duplicateKeys.includes(header.key);
    },

    isHeaderValueValid(header: HeaderObject): boolean {
      return header.value?.length > 0;
    },

    trimHeaderKey(header: HeaderObject, value: string): void {
      header.key = value?.replace(/\s/g, '') || '';
    },

    newAction(): Action {
      const factory = DatasetsUtils.newEntityFactory['actions'];
      return factory && factory();
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedActionData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData as Action, Boolean(entityNotSaved));
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },

    setFieldTouched(name: string) {
      if (!this.fieldsTouched.includes(name)) this.fieldsTouched.push(name);
      if (name in this.$refs) this.$refs[name]?.setIsDirty?.(true);
    },
  },
  created() {
    const tagsStore = useTagsStore();
    this.$watch(
      () => [tagsStore.editorDocId, tagsStore.editorDocType],
      async ([docId, docType]) => {
        if (!docId || docType !== 'actions') {
          return;
        }
        await this.branchesStore.getBranches();
        await this.loadAction(docId);
      },
      { immediate: true }
    );
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Action', 'Actions Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (this.isNew) {
            this.selectedActionData = this.newAction();
            this.selectedActionData.name = Utils.generateUniqueEntityName(
              this.selectedActionData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalActionData = _.cloneDeep(this.selectedActionData);
          } else {
            const actionId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === actionId;
            });
            await this.loadReferencedIds();
            if (dataExists) {
              await this.loadAction(actionId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedActionData = JSON.parse(savedSessionData);
            this.reloadHeadersList();
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>

<style lang="scss" scoped>
.status-code-invalid {
  margin-top: 8px;
}
</style>
